import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Save, Calendar as CalendarIcon, Plus, X, AlertCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import DatePicker from 'react-datepicker';
import BookingSlotSelector from '../components/BookingSlotSelector';

const CreateBooking = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { apiCall } = useAuth();
  
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [bookingType, setBookingType] = useState('service');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  
  // New customer modal state
  const [showNewCustomerModal, setShowNewCustomerModal] = useState(false);
  const [newCustomerData, setNewCustomerData] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  
  // Guest checkout state
  const [isGuestCheckout, setIsGuestCheckout] = useState(false);
  const [guestInfo, setGuestInfo] = useState({
    name: '',
    email: '',
    phone: ''
  });
  
  // Common fields
  const [bookingDate, setBookingDate] = useState(new Date());
  const [bookingTime, setBookingTime] = useState('10:00');
  const [locationType, setLocationType] = useState('online');
  const [locationDetails, setLocationDetails] = useState('');
  const [meetingLink, setMeetingLink] = useState('');
  const [customerNotes, setCustomerNotes] = useState('');
  
  // Service-specific
  const [duration, setDuration] = useState('1 hour');
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [endTime, setEndTime] = useState('11:00');
  
  // Smart Slot Selection state
  const [useSmartSlots, setUseSmartSlots] = useState(true);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [slotValidation, setSlotValidation] = useState(null);
  
  // Rental-specific
  const [rentalEndDate, setRentalEndDate] = useState(new Date());
  const [rentalDuration, setRentalDuration] = useState('1 day');
  
  // Subscription-specific
  const [subscriptionPlan, setSubscriptionPlan] = useState('monthly');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [startDate, setStartDate] = useState(new Date());
  
  // Event-specific
  const [eventDate, setEventDate] = useState(new Date());
  const [ticketQuantity, setTicketQuantity] = useState(1);
  const [ticketType, setTicketType] = useState('general');
  
  // Handle slot selection from BookingSlotSelector
  const handleSlotSelect = (slotData) => {
    setSelectedSlot(slotData);
    setBookingDate(new Date(slotData.date));
    setBookingTime(slotData.time);
    setDurationMinutes(slotData.duration);
    setDuration(`${slotData.duration} minutes`);
    
    // Calculate end time
    const [hours, mins] = slotData.time.split(':').map(Number);
    const endMinutes = hours * 60 + mins + slotData.duration;
    const endHours = Math.floor(endMinutes / 60);
    const endMins = endMinutes % 60;
    setEndTime(`${String(endHours).padStart(2, '0')}:${String(endMins).padStart(2, '0')}`);
  };
  
  // Handle validation result
  const handleValidationResult = (result) => {
    setSlotValidation(result);
    if (!result.is_valid) {
      toast({
        title: 'Slot Not Available',
        description: result.message,
        variant: 'destructive'
      });
    }
  };
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    try {
      const [customersData, servicesData] = await Promise.all([
        apiCall('/api/customers/'),
        apiCall('/api/listings/?status=active')
      ]);
      setCustomers(customersData);
      setServices(servicesData);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleCustomerSelect = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    setSelectedCustomer(customer);
  };
  
  const handleServiceSelect = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    setSelectedService(service);
  };
  
  const handleCreateNewCustomer = async () => {
    if (!newCustomerData.name || !newCustomerData.email || !newCustomerData.password) {
      toast({
        title: 'Error',
        description: 'Please fill all customer fields',
        variant: 'destructive'
      });
      return;
    }
    
    setCreatingCustomer(true);
    try {
      const newCustomer = await apiCall('/api/customers/', {
        method: 'POST',
        body: JSON.stringify(newCustomerData)
      });
      
      // Add to customers list and select it
      setCustomers([...customers, newCustomer]);
      setSelectedCustomer(newCustomer);
      
      // Reset form and close modal
      setNewCustomerData({ name: '', email: '', password: '' });
      setShowNewCustomerModal(false);
      
      toast({
        title: 'Success',
        description: 'Customer created successfully'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create customer',
        variant: 'destructive'
      });
    } finally {
      setCreatingCustomer(false);
    }
  };
  
  const calculatePricing = () => {
    if (!selectedService) return { base_price: 0, tax: 0, service_fee: 0, total: 0 };
    
    // Ensure price is a number
    let basePrice = parseFloat(selectedService.data?.price) || 0;
    
    // Adjust based on booking type
    if (bookingType === 'rental') {
      const days = Math.ceil((rentalEndDate - bookingDate) / (1000 * 60 * 60 * 24));
      basePrice = basePrice * days;
    } else if (bookingType === 'event') {
      basePrice = basePrice * ticketQuantity;
    }
    
    const tax = basePrice * 0.18;
    const serviceFee = basePrice * 0.05;
    const total = basePrice + tax + serviceFee;
    
    return {
      base_price: basePrice,
      tax: tax,
      service_fee: serviceFee,
      total: total
    };
  };
  
  const handleSubmit = async () => {
    // Validate customer or guest info
    if (!isGuestCheckout && !selectedCustomer) {
      toast({
        title: 'Error',
        description: 'Please select a customer',
        variant: 'destructive'
      });
      return;
    }
    
    if (isGuestCheckout && (!guestInfo.name || !guestInfo.email || !guestInfo.phone)) {
      toast({
        title: 'Error',
        description: 'Please fill all guest information',
        variant: 'destructive'
      });
      return;
    }
    
    if (!selectedService) {
      toast({
        title: 'Error',
        description: 'Please select a service',
        variant: 'destructive'
      });
      return;
    }
    
    // Validate slot selection for service bookings with smart slots enabled
    if (bookingType === 'service' && useSmartSlots && !selectedSlot) {
      toast({
        title: 'Error',
        description: 'Please select a time slot from the calendar',
        variant: 'destructive'
      });
      return;
    }
    
    // Double-check slot availability before booking
    if (bookingType === 'service' && useSmartSlots && selectedSlot) {
      try {
        const validationResult = await apiCall('/api/booking-availability/validate', {
          method: 'POST',
          body: JSON.stringify({
            listing_id: selectedService.id,
            date: selectedSlot.date,
            start_time: selectedSlot.time,
            duration_minutes: selectedSlot.duration
          })
        });
        
        if (!validationResult.is_valid) {
          toast({
            title: 'Slot No Longer Available',
            description: validationResult.message,
            variant: 'destructive'
          });
          setSelectedSlot(null);
          return;
        }
      } catch (err) {
        console.error('Validation error:', err);
        // Continue with booking if validation fails (fail-open)
      }
    }
    
    try {
      setSaving(true);
      const pricing = calculatePricing();
      
      let schedule = {};
      if (bookingType === 'service') {
        schedule = {
          date: bookingDate.toISOString().split('T')[0],
          time: bookingTime,
          duration: duration,
          duration_minutes: durationMinutes, // Add duration in minutes for overlap blocking
          end_time: endTime
        };
      } else if (bookingType === 'rental') {
        schedule = {
          date: bookingDate.toISOString().split('T')[0],
          time: bookingTime,
          duration: rentalDuration,
          end_time: rentalEndDate.toISOString().split('T')[0]
        };
      } else if (bookingType === 'subscription') {
        schedule = {
          date: startDate.toISOString().split('T')[0],
          time: '00:00',
          duration: subscriptionPlan,
          end_time: ''
        };
      } else if (bookingType === 'event') {
        schedule = {
          date: eventDate.toISOString().split('T')[0],
          time: bookingTime,
          duration: '3 hours',
          end_time: ''
        };
      }
      
      const bookingData = {
        service: {
          name: selectedService.title,
          category: selectedService.category,
          description: selectedService.description || ''
        },
        customer: isGuestCheckout ? {
          name: guestInfo.name,
          email: guestInfo.email,
          phone: guestInfo.phone
        } : {
          name: selectedCustomer.name,
          email: selectedCustomer.email,
          phone: selectedCustomer.phone || 'N/A'
        },
        vendor: {
          name: 'Admin Created',
          email: 'admin@marketplace.com',
          phone: 'N/A'
        },
        schedule: schedule,
        location: {
          type: locationType,
          details: locationDetails,
          meeting_link: meetingLink
        },
        pricing: pricing,
        payment: {
          method: 'online',
          status: 'pending',
          transaction_id: `TXN-${Date.now()}`,
          paid_on: new Date().toISOString().split('T')[0]
        },
        booking_type: bookingType,
        notes: customerNotes,
        cancellation_policy: 'Free cancellation up to 24 hours before booking'
      };
      
      await apiCall('/api/bookings/', {
        method: 'POST',
        body: JSON.stringify(bookingData)
      });
      
      toast({
        title: 'Success',
        description: 'Booking created successfully'
      });
      
      navigate('/bookings');
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };
  
  const pricing = calculatePricing();
  
  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">Loading...</p>
        </div>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-6xl">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/bookings')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Bookings
          </Button>
        </div>
        
        <div className="flex items-center gap-3">
          <CalendarIcon className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Create New Booking</h1>
            <p className="text-gray-600">Create a booking for a customer</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Booking Type</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { value: 'service', label: 'Service (Time Slot)', icon: '🕒' },
                    { value: 'rental', label: 'Rental Booking', icon: '🚗' },
                    { value: 'subscription', label: 'Subscription', icon: '📅' },
                    { value: 'event', label: 'Event Ticket', icon: '🎫' }
                  ].map(type => (
                    <button
                      key={type.value}
                      onClick={() => setBookingType(type.value)}
                      className={`p-4 border-2 rounded-lg text-center transition-all ${
                        bookingType === type.value
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-3xl mb-2">{type.icon}</div>
                      <div className="text-sm font-medium">{type.label}</div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Customer & Service</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2 mb-4 p-3 bg-gray-50 rounded-lg">
                  <input
                    type="checkbox"
                    id="guestCheckout"
                    checked={isGuestCheckout}
                    onChange={(e) => setIsGuestCheckout(e.target.checked)}
                    className="h-4 w-4"
                  />
                  <Label htmlFor="guestCheckout" className="cursor-pointer">
                    Guest Checkout (No account required)
                  </Label>
                </div>
                
                {!isGuestCheckout ? (
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label>Select Customer *</Label>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setShowNewCustomerModal(true)}
                        type="button"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add New
                      </Button>
                    </div>
                    <Select 
                      onValueChange={handleCustomerSelect}
                      value={selectedCustomer?.id}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Choose a customer" />
                      </SelectTrigger>
                      <SelectContent>
                        {customers.map(customer => (
                          <SelectItem key={customer.id} value={customer.id}>
                            {customer.name} ({customer.email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedCustomer && (
                      <p className="text-sm text-gray-500">
                        Selected: {selectedCustomer.name}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4 p-4 border rounded-lg bg-blue-50">
                    <h4 className="font-semibold text-sm">Guest Information</h4>
                    <div className="space-y-2">
                      <Label>Full Name *</Label>
                      <Input
                        value={guestInfo.name}
                        onChange={(e) => setGuestInfo({...guestInfo, name: e.target.value})}
                        placeholder="Enter guest name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={guestInfo.email}
                        onChange={(e) => setGuestInfo({...guestInfo, email: e.target.value})}
                        placeholder="guest@example.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Phone *</Label>
                      <Input
                        type="tel"
                        value={guestInfo.phone}
                        onChange={(e) => setGuestInfo({...guestInfo, phone: e.target.value})}
                        placeholder="+91 99999 99999"
                      />
                    </div>
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label>Select Service/Listing *</Label>
                  <Select onValueChange={handleServiceSelect}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map(service => (
                        <SelectItem key={service.id} value={service.id}>
                          {service.title} - ₹{service.data?.price || 0}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
            
            {/* Booking Type Specific Fields */}
            {bookingType === 'service' && (
              <div className="space-y-4">
                {/* Toggle between smart slots and manual entry */}
                <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={useSmartSlots}
                      onChange={(e) => setUseSmartSlots(e.target.checked)}
                      className="h-4 w-4 text-indigo-600 rounded"
                    />
                    <span className="text-sm font-medium">Use Smart Slot Selection</span>
                  </label>
                  <span className="text-xs text-gray-500">
                    (Automatically blocks overlapping times)
                  </span>
                </div>
                
                {useSmartSlots && selectedService ? (
                  <>
                    <BookingSlotSelector
                      listingId={selectedService.id}
                      variants={selectedService.data?.variants || []}
                      defaultDuration={selectedService.data?.duration || 60}
                      onSlotSelect={handleSlotSelect}
                      onValidationResult={handleValidationResult}
                    />
                    
                    {/* Show validation warning if slot has conflicts */}
                    {slotValidation && !slotValidation.is_valid && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-red-800">Slot Conflict</p>
                          <p className="text-sm text-red-700">{slotValidation.message}</p>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <Card>
                    <CardHeader>
                      <CardTitle>Time Slot Booking</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Date *</Label>
                          <DatePicker
                            selected={bookingDate}
                            onChange={setBookingDate}
                            dateFormat="yyyy-MM-dd"
                            minDate={new Date()}
                            className="w-full p-2 border rounded-md"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Start Time *</Label>
                          <Input
                            type="time"
                            value={bookingTime}
                            onChange={(e) => setBookingTime(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Duration</Label>
                          <Select value={duration} onValueChange={(val) => {
                            setDuration(val);
                            // Parse duration to minutes
                            if (val.includes('hour')) {
                              const hours = parseInt(val);
                              setDurationMinutes(hours * 60);
                            } else if (val.includes('minute')) {
                              setDurationMinutes(parseInt(val));
                            }
                          }}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="30 minutes">30 minutes</SelectItem>
                              <SelectItem value="1 hour">1 hour</SelectItem>
                              <SelectItem value="2 hours">2 hours</SelectItem>
                              <SelectItem value="3 hours">3 hours</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>End Time *</Label>
                          <Input
                            type="time"
                            value={endTime}
                            onChange={(e) => setEndTime(e.target.value)}
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
            
            {bookingType === 'rental' && (
              <Card>
                <CardHeader>
                  <CardTitle>Rental Booking</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Start Date *</Label>
                      <DatePicker
                        selected={bookingDate}
                        onChange={setBookingDate}
                        dateFormat="yyyy-MM-dd"
                        minDate={new Date()}
                        className="w-full p-2 border rounded-md"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>End Date *</Label>
                      <DatePicker
                        selected={rentalEndDate}
                        onChange={setRentalEndDate}
                        dateFormat="yyyy-MM-dd"
                        minDate={bookingDate}
                        className="w-full p-2 border rounded-md"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Rental Duration</Label>
                    <Select value={rentalDuration} onValueChange={setRentalDuration}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1 day">1 day</SelectItem>
                        <SelectItem value="3 days">3 days</SelectItem>
                        <SelectItem value="1 week">1 week</SelectItem>
                        <SelectItem value="1 month">1 month</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {bookingType === 'subscription' && (
              <Card>
                <CardHeader>
                  <CardTitle>Subscription Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Subscription Plan *</Label>
                    <Select value={subscriptionPlan} onValueChange={setSubscriptionPlan}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Weekly Plan</SelectItem>
                        <SelectItem value="monthly">Monthly Plan</SelectItem>
                        <SelectItem value="quarterly">Quarterly Plan</SelectItem>
                        <SelectItem value="yearly">Yearly Plan</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Billing Cycle</Label>
                    <Select value={billingCycle} onValueChange={setBillingCycle}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                        <SelectItem value="yearly">Yearly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Start Date *</Label>
                    <DatePicker
                      selected={startDate}
                      onChange={setStartDate}
                      dateFormat="yyyy-MM-dd"
                      minDate={new Date()}
                      className="w-full p-2 border rounded-md"
                    />
                  </div>
                </CardContent>
              </Card>
            )}
            
            {bookingType === 'event' && (
              <Card>
                <CardHeader>
                  <CardTitle>Event Ticket Booking</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Event Date *</Label>
                      <DatePicker
                        selected={eventDate}
                        onChange={setEventDate}
                        dateFormat="yyyy-MM-dd"
                        minDate={new Date()}
                        className="w-full p-2 border rounded-md"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Event Time *</Label>
                      <Input
                        type="time"
                        value={bookingTime}
                        onChange={(e) => setBookingTime(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Number of Tickets *</Label>
                      <Input
                        type="number"
                        min="1"
                        value={ticketQuantity}
                        onChange={(e) => setTicketQuantity(parseInt(e.target.value) || 1)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Ticket Type</Label>
                      <Select value={ticketType} onValueChange={setTicketType}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">General</SelectItem>
                          <SelectItem value="vip">VIP</SelectItem>
                          <SelectItem value="premium">Premium</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            
            <Card>
              <CardHeader>
                <CardTitle>Location Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Location Type</Label>
                  <Select value={locationType} onValueChange={setLocationType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="online">Online</SelectItem>
                      <SelectItem value="provider">Provider Location</SelectItem>
                      <SelectItem value="customer">Customer Location</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {locationType === 'online' && (
                  <div className="space-y-2">
                    <Label>Meeting Link</Label>
                    <Input
                      placeholder="https://meet.google.com/..."
                      value={meetingLink}
                      onChange={(e) => setMeetingLink(e.target.value)}
                    />
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label>Location Details</Label>
                  <Textarea
                    placeholder="Enter address or additional details..."
                    value={locationDetails}
                    onChange={(e) => setLocationDetails(e.target.value)}
                    rows={3}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Customer Notes</Label>
                  <Textarea
                    placeholder="Any special requirements..."
                    value={customerNotes}
                    onChange={(e) => setCustomerNotes(e.target.value)}
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Right Column - Summary */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Booking Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {selectedService && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="font-medium">{selectedService.title}</p>
                    <p className="text-sm text-gray-600">{selectedService.category}</p>
                  </div>
                )}
                
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Base Price:</span>
                    <span className="font-semibold">₹{pricing.base_price.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Tax (18%):</span>
                    <span className="font-semibold">₹{pricing.tax.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Service Fee:</span>
                    <span className="font-semibold">₹{pricing.service_fee.toFixed(2)}</span>
                  </div>
                  <div className="border-t pt-2 flex justify-between text-lg">
                    <span className="font-bold">Total:</span>
                    <span className="font-bold">₹{pricing.total.toFixed(2)}</span>
                  </div>
                </div>
                
                <Button 
                  className="w-full" 
                  onClick={handleSubmit}
                  disabled={saving}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Creating Booking...' : 'Create Booking'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
        
        {/* New Customer Modal */}
        {showNewCustomerModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Create New Customer</h2>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowNewCustomerModal(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Customer Name *</Label>
                  <Input
                    value={newCustomerData.name}
                    onChange={(e) => setNewCustomerData({...newCustomerData, name: e.target.value})}
                    placeholder="Enter customer name"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={newCustomerData.email}
                    onChange={(e) => setNewCustomerData({...newCustomerData, email: e.target.value})}
                    placeholder="customer@example.com"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Password *</Label>
                  <Input
                    type="password"
                    value={newCustomerData.password}
                    onChange={(e) => setNewCustomerData({...newCustomerData, password: e.target.value})}
                    placeholder="Enter password"
                  />
                  <p className="text-xs text-gray-500">Customer will use this to login</p>
                </div>
                
                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setShowNewCustomerModal(false)}
                    disabled={creatingCustomer}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCreateNewCustomer}
                    disabled={creatingCustomer}
                    className="flex-1"
                  >
                    {creatingCustomer ? 'Creating...' : 'Create Customer'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default CreateBooking;
